/**
 * src/pages/SettingsPage.tsx — Paramètres de l'application
 *
 * Orchestrateur : gère les onglets, les données partagées (clients/sites) et les
 * mutations associées. Chaque onglet est un composant dédié dans src/pages/settings/.
 */

import { useState, useEffect, useCallback } from "react";
import ConfirmModal, { type ConfirmModalProps } from "../components/ConfirmModal";
import {
  listClients, upsertClient, deleteClient,
  listSites,   upsertSite,   deleteSite,
  type ClientRecord, type SiteRecord,
} from "../api/settingsApi";
import { type UserRole, getPermissions } from "../auth/roles";
import { ClientsTab } from "./settings/ClientsTab";
import { SitesTab }   from "./settings/SitesTab";
import { MetersTab }  from "./settings/MetersTab";
import { RolesTab }   from "./settings/RolesTab";
import { SystemTab }  from "./settings/SystemTab";

// Tooltip CSS injected once
const TOOLTIP_STYLE = `
.id-tooltip-wrapper:hover .id-tooltip-text { opacity: 1 !important; pointer-events: auto !important; }
`;
if (typeof document !== "undefined" && !document.getElementById("settings-tooltip-css")) {
  const s = document.createElement("style");
  s.id = "settings-tooltip-css";
  s.textContent = TOOLTIP_STYLE;
  document.head.appendChild(s);
}

// ── Re-export shims (backward compat) ─────────────────────────────────────────
export interface CustomSite {
  id: string; clientName: string; name: string;
  description: string; schemaCVC?: string; coverProps: Record<string,string>;
}
export function loadCustomSites(): CustomSite[] { return []; }
export function saveCustomSites(_: CustomSite[]): void {}

// ── Main ───────────────────────────────────────────────────────────────────────
type Tab = "clients"|"sites"|"roles"|"system"|"meters";

interface SettingsPageProps {
  userRole?:              UserRole;
  initialTab?:            string;
  onMaintenanceChange?:   (active: boolean) => void;
}

export default function SettingsPage({ userRole = "admin", initialTab, onMaintenanceChange }: SettingsPageProps) {
  const perms = getPermissions(userRole);
  const defaultTab: Tab = perms.canManageClients ? "clients" : perms.canManageSites ? "sites" : "roles";
  const [tab,     setTab]     = useState<Tab>((initialTab as Tab | undefined) ?? defaultTab);

  useEffect(() => { if (initialTab) setTab(initialTab as Tab); }, [initialTab]);
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [sites,   setSites]   = useState<SiteRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string|null>(null);
  const [modal,   setModal]   = useState<ConfirmModalProps|null>(null);
  const [toast,   setToast]   = useState<string|null>(null);

  const showToast = (msg:string) => { setToast(msg); setTimeout(()=>setToast(null),2500); };

  // Rafraîchissement en place (bouton ↻ Rafraîchir des onglets Clients/Sites) — ne touche pas
  // `loading`, contrairement à `load()` : la liste reste montée/visible pendant le refetch, au
  // lieu de disparaître derrière l'écran "Chargement…" (comportement réservé au montage initial).
  const refresh = useCallback(async () => {
    try {
      const [c,s] = await Promise.all([listClients(), listSites()]);
      setClients(c); setSites(s);
    } catch(e){ setError((e as Error).message); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    await refresh();
    setLoading(false);
  }, [refresh]);
  useEffect(()=>{ load(); },[load]);

  // ── Mutations partagées (clients / sites) ─────────────────────────────────
  const handleSaveClient = async (c:ClientRecord) => {
    const saved = await upsertClient(c);
    setClients(prev=>{
      const i=prev.findIndex(x=>x.id===saved.id);
      return i>=0 ? prev.map((x,j)=>j===i?saved:x) : [...prev,saved];
    });
  };

  const handleDeleteClient = async (c:ClientRecord) => {
    await deleteClient(c.id);
    setClients(p=>p.filter(x=>x.id!==c.id));
    setSites(p=>p.filter(x=>x.clientId!==c.id));
  };

  const handleSaveSite = async (s:SiteRecord) => {
    const saved = await upsertSite(s);
    setSites(prev=>{
      const i=prev.findIndex(x=>x.id===saved.id);
      return i>=0 ? prev.map((x,j)=>j===i?saved:x) : [...prev,saved];
    });
  };

  const handleDeleteSite = async (s:SiteRecord) => {
    await deleteSite(s.id);
    setSites(p=>p.filter(x=>x.id!==s.id));
  };

  const TABS:{id:Tab;label:string;icon:string}[]=[
    ...(perms.canManageClients        ? [{id:"clients" as Tab, label:"Clients", icon:"🏢"}] : []),
    ...(perms.canManageSites          ? [{id:"sites"   as Tab, label:"Sites",   icon:"🏗"}] : []),
    ...(perms.canManageSites          ? [{id:"meters"  as Tab, label:"PDL / PCE",          icon:"⚡"}] : []),
    ...(perms.canManageRoles          ? [{id:"roles"   as Tab, label:"Rôles",          icon:"🔑"}] : []),
    ...(userRole === "super-admin"    ? [{id:"system"  as Tab, label:"Système",                    icon:"⚙️"}] : []),
  ];

  return (
    <div className="screen" style={{height:"100%",overflow:"hidden",paddingBottom:0}}>
      <div className="screen-inner" style={{height:"100%",display:"flex",flexDirection:"column"}}>

        {/* ── Titre (fixe) ── */}
        <div style={{flexShrink:0}}>
          <h1 className="page-title mb-28">Paramètres</h1>
        </div>

        {/* ── Sidebar onglets (gauche) + contenu (droite) ─────────────────── */}
        <div style={{flex:1,minHeight:0,display:"flex",gap:28}}>

          {/* Sidebar */}
          <div style={{flexShrink:0,width:180,display:"flex",flexDirection:"column",gap:2,
            borderRight:"1px solid var(--border-light)",paddingRight:16}}>
            {TABS.map(t=>(
              <button key={t.id} onClick={()=>setTab(t.id)}
                style={{background:tab===t.id?"var(--green-dim)":"none",
                  border:"none",cursor:"pointer",padding:"10px 14px",borderRadius:8,
                  fontFamily:"var(--ff-title)",fontSize:13,fontWeight:700,textAlign:"left",
                  color:tab===t.id?"#fff":"var(--text-muted)",
                  display:"flex",alignItems:"center",gap:10,transition:"all 0.15s"}}>
                <span>{t.icon}</span> {t.label}
              </button>
            ))}
          </div>

          {/* Contenu */}
          <div style={{flex:1,minHeight:0,display:"flex",flexDirection:"column"}}>
            {loading && <div style={{textAlign:"center",padding:40,color:"var(--text-muted)"}}>Chargement…</div>}
            {error   && <div style={{background:"#fdecea",border:"1px solid #f5c6cb",borderRadius:8,padding:"12px 16px",marginBottom:20,fontSize:13,color:"var(--red)",flexShrink:0}}>
              ⚠ {error} <button onClick={load} style={{marginLeft:12,background:"none",border:"none",cursor:"pointer",color:"var(--red)",textDecoration:"underline"}}>Réessayer</button>
            </div>}

            <div style={{flex:1,minHeight:0}}>
              {!loading && tab==="clients" && (
                <ClientsTab clients={clients} sites={sites}
                  onSave={handleSaveClient} onDelete={handleDeleteClient} onRefresh={refresh}
                  setModal={setModal} showToast={showToast}/>
              )}

              {!loading && tab==="sites" && (
                <SitesTab clients={clients} sites={sites}
                  onSave={handleSaveSite} onDelete={handleDeleteSite} onRefresh={refresh}
                  setModal={setModal} showToast={showToast} userRole={userRole}/>
              )}

              {!loading && tab==="meters" && (
                <MetersTab clients={clients} sites={sites}
                  setModal={setModal} showToast={showToast} canEditSge={perms.canManageRoles}
                  canEditEngieSource={perms.canManageRoles}/>
              )}

              {tab==="system" && (
                <SystemTab setModal={setModal} showToast={showToast} onMaintenanceChange={onMaintenanceChange}/>
              )}

              {tab==="roles" && (
                <RolesTab showToast={showToast}/>
              )}
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <div style={{position:"fixed",bottom:24,left:"50%",transform:"translateX(-50%)",
          background:"#1a1a2e",color:"#fff",padding:"10px 20px",borderRadius:8,
          fontSize:13,boxShadow:"0 4px 16px rgba(0,0,0,0.3)",zIndex:9999}}>
          {toast}
        </div>
      )}
      {modal && <ConfirmModal {...modal}/>}
    </div>
  );
}
